import { useCallback, useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { NAVIGATION_KEYS } from 'src/interfaces/enums';
import useUI from 'src/hooks/useUI';
import useClickOutside, { ClickOutsideEvent } from 'src/hooks/useClickOutside';
import useMenuRoute from 'src/themes/shared/containers/MenuRouteContainer/hooks/useMenuRoute';
import MenuRoute from 'src/themes/shared/containers/MenuRouteContainer/components/MenuRoute';
import { withMenuRouteContextProvider } from 'src/themes/shared/containers/MenuRouteContainer/context/MenuRouteContext';
import SwitchMenu from 'src/themes/shared/containers/MenuRouteContainer/containers/SwitchMenu';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { MENU_ROUTES } from './routes';

import HomeSettingControlContainer from './containers/HomeSettingControlContainer';
import ResolutionControlContainer from './containers/ResolutionControlContainer';
import PlaybackRateControlContainer from './containers/PlaybackRateControlContainer';

const DELAY_TO_HIDE_SETTINGS = 100;

const SettingsOverlay = () => {
  const { t } = useTranslation('common');
  const { goBack } = useMenuRoute();

  const { hideSettings } = useUI();
  const [isPreparedToHide, setIsPreparedToHide] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: ClickOutsideEvent) => {
    event.stopPropagation();

    setIsPreparedToHide(true);
  }, []);

  useClickOutside(containerRef?.current, handleClickOutside);

  useEffect(() => {
    if (!isPreparedToHide) {
      return;
    }
    const timeout = setTimeout(hideSettings, DELAY_TO_HIDE_SETTINGS);

    return () => {
      clearTimeout(timeout);
    };
  }, [isPreparedToHide, hideSettings]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const { key } = event;

      switch (key) {
        case NAVIGATION_KEYS.ESCAPE:
          hideSettings();
          break;
        case NAVIGATION_KEYS.ARROW_LEFT:
          goBack();
          break;
      }
    }

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [hideSettings, goBack]);

  return (
    <SwitchMenu ref={containerRef} className={classNames(styles.container)} onClose={hideSettings}>
      <MenuRoute title={t('settings.main')} path={MENU_ROUTES.HOME}>
        <HomeSettingControlContainer />
      </MenuRoute>
      <MenuRoute title={t('settings.quality')} path={MENU_ROUTES.RESOLUTION} showBackButton>
        <ResolutionControlContainer />
      </MenuRoute>
      <MenuRoute title={t('settings.speed')} path={MENU_ROUTES.PLAYBACK_RATE} showBackButton>
        <PlaybackRateControlContainer />
      </MenuRoute>
    </SwitchMenu>
  );
};

export default withMenuRouteContextProvider(SettingsOverlay);
